/* global google */
import React, { useEffect, useMemo, useState } from "react"
import { injectIntl } from "react-intl"
import { Switch, Route } from "react-router-dom"
import { withRouter } from "react-router"
import clsx from "clsx"
import { InfoWindow, Marker } from "@react-google-maps/api"
import { Avatar, Backdrop, CircularProgress, Grid, Tooltip } from "@material-ui/core"
import useMediaQuery from "@material-ui/core/useMediaQuery"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import AccountCircle from "@material-ui/icons/AccountCircle"
import MenuIcon from "@material-ui/icons/Menu"

import CreateRental from "../../components/CreateRental"
import MapContainer from "../../components/MapContainer"
import Menu from "../../components/Menu"
import MenuBar from "../../components/MenuBar"
import MyTrips from "../../components/MyTrips"
import { Ridecell, Carsharing } from "@ridecell/web-sdk"
import {
  Box,
  ProfileSwitcher,
  ThemeProvider,
  Typography,
} from "@ridecell/web-components-material"

import { formatActiveProfile } from "../../utils/formatActiveProfile"
import useGlobalContext from "../../utils/customHooks/useGlobalContext"
import PopupModal from "../../utils/elements/PopupModal"
import ProfilePanel from "../../components/ProfilePanel"
import messages from "./messages"
import { getIncompleteRegistrationStep } from "../../utils/common"
import PaymentMethodModal from "../../components/ProfilePanel/PaymentMethodModal"

const returnUrlForStripe = `https://${window.location.host}/reserve/`

const PROFILE_TYPES = {
  HYBRID: "HYBRID",
  BUSINESS: "BUSINESS",
  PERSONAL: "PERSONAL",
}

const REGISTRATION_STEP_KEYS = {
  PERSONAL: "personal",
  BUSINESS: "business",
}

const drawerWidth = 240
const useStyles = makeStyles((theme) => ({
  businessAvatar: {
    position: "fixed",
    top: 10,
    right: 68,
    height: 50,
    width: 50,
    backgroundColor: "#f0f0f0",
    border: "2px solid #ddd",
    cursor: "pointer",
  },
  content: {
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(3),
      maxHeight: "88vh",
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0),
      height: "60vh",
    },
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflow: "auto",
  },
  contentShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: drawerWidth,
  },
  infoWindowHeader: {
    marginTop: 0,
    fontSize: 18,
  },
  loadingBackdropContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  loadingBackdrop: {
    // mui zIndex values are ridiculously high -
    // https://mui.com/material-ui/customization/z-index/
    zIndex: 1600,
  },
  loadingBackdropText: {
    color: "#fff",
    marginTop: "100px",
  },
  loggedIn: {
    color: "#3f51b5",
  },
  loginAvatar: {
    position: "fixed",
    top: 10,
    right: 10,
    "&:hover": {
      backgroundColor: "#989898",
      boxShadow:
        "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
      cursor: "pointer",
    },
    height: "48px",
    width: "48px",
  },
  menuButtonMobile: {
    position: "fixed",
    top: 10,
    left: 10,
  },
  profileSwitcher: { position: "fixed", top: 10, right: 68 },
}))

const HomePage = ({ history, intl: { formatMessage } }) => {
  const classes = useStyles()
  const [accountProfileOptions, setAccountProfileOptions] = useState([])
  const [activeProfile, setActiveProfile] = useState(null)
  const [markets, setMarkets] = useState([])
  const [selectedMarket, setSelectedMarket] = useState("")
  const [mapCenter, setMapCenter] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [openLoginDialog, setOpenLoginDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [logoutDialog, setLogoutDialog] = useState(false)
  const [registrationSteps, setRegistrationSteps] = useState(null)
  const [selectedStation, setSelectedStation] = useState(null)
  const [stations, setStations] = useState([])
  const [stationWindow, setStationWindow] = useState(null)
  const {
    state: {
      isAuthenticated,
      sdkInitialized,
      tenantSettings,
      customer,
      tenantId,
      error,
      concentDecisions,
    },
    setAuthenticated,
    setCurrentUser,
    setMfaDialog,
    toggleBackdrop,
    setError,
    setConsentDecisions,
  } = useGlobalContext()
  const theme = useTheme()
  const [openProfilePanel, setOpenProfilePanel] = useState(
    history.location?.state?.openProfilePanel || false
  )
  const [openPaymentModal, togglePaymentModal] = React.useState(false)

  const query = new URLSearchParams(window.location.search)
  const clientSecret = query.get("setup_intent_client_secret")

  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const rentalMode = useMemo(
    () =>
      activeProfile && {
        rental_mode: activeProfile.hasOwnProperty("business_info")
          ? "business"
          : "personal",
      },
    [activeProfile]
  )

  const localStorageCustomer = localStorage.getItem("customer")
  const localStorageAuthToken = localStorage.getItem("authToken")

  const handleCloseLogout = () => setLogoutDialog(false)
  const handleCloseLoginDialog = () => setOpenLoginDialog(false)
  const handleOpenLoginDialog = () => {
    if (isAuthenticated) {
      setLogoutDialog(true)
    } else {
      setOpenLoginDialog(true)
    }
  }
  const handleDrawerOpen = () => {
    setMenuOpen(true)
  }
  const handleDrawerClose = () => {
    setMenuOpen(false)
  }

  // Fetch Customer
  const fetchCustomer = async () => {
    try {
      toggleBackdrop(true)
      const customer = await Ridecell.getCustomer({
        fetchUpdatedCustomer: true,
      })
      setCurrentUser(customer)
      setActiveProfile(formatActiveProfile(customer))
      setAccountProfileOptions(getProfileList(customer))
    } catch (e) {
      setError(e)
    } finally {
      toggleBackdrop(false)
    }
  }

  useEffect(() => {
    if (
      isAuthenticated ||
      (localStorageCustomer && localStorageAuthToken && customer)
    ) {
      fetchCustomer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  useEffect(() => {
    ;(async () => {
      // showing switcher loader when actually user switches between profiles
      const isLoadingRequired = accountProfileOptions.length > 1
      setLoading(isLoadingRequired)
      await getMarkets()
      setLoading(false)
    })()
  }, [activeProfile])

  // Fetch Markets after SDK initialized
  useEffect(() => {
    ;(async () => {
      if (!sdkInitialized || !isAuthenticated) return
      toggleBackdrop(true)
      await getMarkets()
      toggleBackdrop(false)
    })()
  }, [sdkInitialized, isAuthenticated])

  const getMarkets = async () => {
    try {
      const response = await Carsharing.getMarkets()
      const results = response?.results
      setMarkets(results)
      setSelectedMarket(results[0])
      setMapCenter({
        lat:
          results[0].display_location && results[0].display_location.latitude,
        lng:
          results[0].display_location && results[0].display_location.longitude,
      })
    } catch (e) {
      setError(e)
    } finally {
    }
  }

  // Fetch new stations when market changes
  useEffect(() => {
    if (!selectedMarket) return
    const fetchStations = async () => {
      await getStations(selectedMarket)
    }
    fetchStations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarket])

  const getStations = (market) =>
    new Promise((resolve, reject) => {
      Carsharing.getStations({
        market_id: market.id,
        ...rentalMode,
        page_size: 500,
      })
        .then((response) => {
          setStations(response)
          resolve(response)
        })
        .catch((error) => {
          reject(error)
        })
    })

  const getConsentOptions = async () => {
    toggleBackdrop(true)
    try {
      const resp = await Ridecell.getConsentOptions({
        user_id: customer?.uuid,
        market_id: selectedMarket?.id,
        page: 1,
        page_size: 25,
        tenant_id: tenantId,
      })
      setConsentDecisions(resp.results)
    } catch (e) {
      setError(e)
    }
    toggleBackdrop(false)
  }

  useEffect(() => {
    if (isAuthenticated && customer) {
      getConsentOptions()
    }
  }, [isAuthenticated])

  // BE doesn't provide a list of account profiles
  const getProfileList = (customer) => {
    const {
      business_entity,
      business_email,
      business_registration_completed,
      email,
      first_name,
      id,
      last_name,
    } = customer
    const profileArray = []

    if (email) {
      const avatar =
        first_name && last_name && first_name !== "" && last_name !== ""
          ? `${first_name[0]
              .trim()
              .toUpperCase()}${last_name[0].trim().toUpperCase()}`
          : null
      // TODO: Needs formatting once localization is added
      const name = formatMessage(messages.personal)
      profileArray.push({ avatar, email, id: +id, name })
    }

    if (business_email) {
      profileArray.push({
        avatar: business_entity.brand_logo ? (
          <img
            alt="brand_logo"
            src={business_entity.brand_logo}
            height="42px"
            width="42px"
          />
        ) : null,
        business_info: {
          business_email,
          business_entity,
          business_registration_completed,
        },
        id: +business_entity.id,
        name: business_entity.legal_name,
      })
    }

    return profileArray
  }

  const login = async ({ username, password, deviceId }) =>
    new Promise((res, rej) => {
      Ridecell.login({
        email: username,
        password,
        deviceId,
      })
        .then(async (response) => {
          if (response["nonce"]) {
            setMfaDialog(true)
          } else if (!response["is_staff"]) {
            setAuthenticated(true)
            history.push("/")
          }
          res(response)
        })
        .catch((result) => {
          setAuthenticated(false)
          rej(result)
        })
    })

  const logout = async () => {
    toggleBackdrop(true)
    await Ridecell.logout()
      .then(async () => {
        setCurrentUser(null)
        setAuthenticated(false)
        setMfaDialog(false)
        setStations([])
        setMarkets([])
        handleCloseLogout()
        setOpenProfilePanel(false)
        history.push("/")
      })
      .catch((result) => {})
    toggleBackdrop(false)
  }

  const initials = () => {
    if (!customer || !isAuthenticated) return
    const { first_name, last_name } = customer
    if (first_name && last_name && first_name !== "" && last_name !== "") {
      return `${first_name[0]
        .trim()
        .toUpperCase()}${last_name[0].trim().toUpperCase()}`
    }
    // If customer's name is jacked, we need to show something that they are logged in
    return <AccountCircle />
  }

  const selectStation = (station) => {
    setStationWindow(station)
    setSelectedStation(station)
  }

  const onPostPaymentMethodCreation = () => {
    togglePaymentModal(false)
    setOpenProfilePanel(true)
  }

  const passwordResetType =
    tenantSettings && tenantSettings.passwordResetNotificationType

  useEffect(() => {
    ;(async () => {
      if (customer && !openProfilePanel) {
        try {
          const resp = await Ridecell.getRegistrationSteps({
            customerId: customer.id,
            queryParams: { page: 1, page_size: 100, tenant_id: tenantId },
          })

          setRegistrationSteps(resp)
        } catch (e) {}
      }
    })()
  }, [customer])

  //* Redirecting the customer to registration to complete all the steps
  useEffect(() => {
    if (registrationSteps && customer) {
      toggleBackdrop(true)
      try {
        const profileMode = customer?.profile_type?.toLowerCase()
        let customerProfileMode =
          (rentalMode && rentalMode.rental_mode) ||
          (profileMode === REGISTRATION_STEP_KEYS.PERSONAL ? REGISTRATION_STEP_KEYS.PERSONAL : REGISTRATION_STEP_KEYS.BUSINESS)
        const profileSteps = registrationSteps[customerProfileMode]
        const step = getIncompleteRegistrationStep({
          customer,
          registrationSteps: profileSteps,
          tenantSettings,
        })
        if (step) {
          history.push({
            pathname: `/signup`,
            state: {
              currentStep: step,
              selectedProfileMode: customerProfileMode,
            },
          })
        }
      } catch (e) {
        // handle error
      } finally {
        toggleBackdrop(false)
      }
    }
  }, [registrationSteps, activeProfile])

  useEffect(() => {
    if (clientSecret) {
      togglePaymentModal(true)
    }
  }, [clientSecret])

  return (
    <React.Fragment>
      <ThemeProvider>
        <React.Fragment>
          <Grid container direction={isMobile ? "column-reverse" : "row"}>
            <Grid item xs={12} lg={4}>
              <MenuBar
                closeLogin={handleCloseLoginDialog}
                closeLogout={handleCloseLogout}
                handleDrawerOpen={handleDrawerOpen}
                isMobile={isMobile}
                login={login}
                logout={logout}
                openLogin={openLoginDialog}
                openLogout={logoutDialog}
                open={menuOpen}
                passwordResetType={passwordResetType}
                history={history}
                tenantSettings={tenantSettings}
              />
              <main
                className={clsx(classes.content, {
                  [classes.contentShift]: menuOpen,
                })}
              >
                <Switch>
                  <Route path="/trips">
                    <MyTrips customer={customer} rentalMode={rentalMode} />
                  </Route>
                  <Route path="/">
                    <CreateRental
                      customer={customer}
                      login={handleOpenLoginDialog}
                      activeProfile={activeProfile}
                      markets={markets}
                      rentalMode={rentalMode}
                      stations={stations}
                      selectedStation={selectedStation}
                      setSelectedMarket={setSelectedMarket}
                      selectedMarket={selectedMarket}
                      setMapCenter={setMapCenter}
                      userLoggedIn={isAuthenticated || customer}
                    />
                  </Route>
                </Switch>
              </main>
            </Grid>
            <Grid item xs={12} lg={8}>
              <MapContainer
                isMobile={isMobile}
                mapCenter={mapCenter}
                tenantId={tenantId}
              >
                {stationWindow && (
                  <InfoWindow
                    onCloseClick={() => setStationWindow(null)}
                    position={{
                      lat: stationWindow.location.lat,
                      lng: stationWindow.location.lng,
                    }}
                    options={{ pixelOffset: new google.maps.Size(-2, -54) }}
                  >
                    <div>
                      <h1 className={classes.infoWindowHeader}>
                        {stationWindow.name}
                      </h1>
                      <div>
                        {stationWindow.location &&
                          stationWindow.location.display_name}
                      </div>
                    </div>
                  </InfoWindow>
                )}
                {stations.length > 0 &&
                  stations.map((station) => (
                    <Marker
                      key={station.id}
                      position={{
                        lat: station.location.lat,
                        lng: station.location.lng,
                      }}
                      icon={
                        "https://s3-us-west-2.amazonaws.com/uidemos.ridecell.com/test_drive_website/Pin_Blue.png"
                      }
                      onClick={() => selectStation(station)}
                    />
                  ))}
              </MapContainer>
            </Grid>
            {isMobile && (
              <Avatar
                className={classes.menuButtonMobile}
                onClick={handleDrawerOpen}
              >
                <MenuIcon />
              </Avatar>
            )}
            {activeProfile && customer?.profile_type === PROFILE_TYPES.HYBRID && (
              <ProfileSwitcher
                activeProfile={activeProfile}
                ButtonProps={{ size: "small" }}
                className={classes.profileSwitcher}
                items={accountProfileOptions}
                MenuItemProps={{
                  onClick: (e) => {
                    setLoading(true)
                    const activeProfile = accountProfileOptions.find(
                      (profile) => profile.id === e.currentTarget.value
                    )
                    setActiveProfile(activeProfile)
                    setLoading(false)
                  },
                }}
              />
            )}
            {activeProfile && customer?.profile_type === PROFILE_TYPES.BUSINESS && (
              <Tooltip title={activeProfile.name}>
                <Avatar
                  alt={activeProfile.name}
                  className={classes.businessAvatar}
                  src={activeProfile.business_info?.business_entity?.brand_logo}
                />
              </Tooltip>
            )}
             
            <Avatar
              className={clsx(classes.loginAvatar, {
                [classes.loggedIn]: isAuthenticated && customer,
              })}
              onClick={() =>
                isAuthenticated && customer
                  ? setOpenProfilePanel(true)
                  : handleOpenLoginDialog()
              }
            >
              {initials()}
            </Avatar>
            <Menu
              handleDrawerClose={handleDrawerClose}
              open={menuOpen}
              tenantSettings={tenantSettings}
            />
          </Grid>
          <Backdrop className={classes.loadingBackdrop} open={loading}>
            <Box className={classes.loadingBackdropContainer}>
              <CircularProgress color="inherit" />
              <Typography
                className={classes.loadingBackdropText}
                component="div"
                variant="body1"
              >
                {formatMessage(messages.switchingMessage, {
                  profileType:
                    activeProfile && activeProfile.business_info
                      ? activeProfile.business_info.business_entity.legal_name
                      : "personal",
                })}
              </Typography>
            </Box>
          </Backdrop>
          {error.hasError && (
            <PopupModal
              hasError={error.hasError}
              errorMessage={error.errorMessage}
              onClose={() => setError()}
            />
          )}
        </React.Fragment>
      </ThemeProvider>
      {isAuthenticated && customer && (
        <ProfilePanel
          clientSecret={clientSecret}
          closePanel={() => setOpenProfilePanel(false)}
          concentDecisions={concentDecisions}
          fetchUpdateCustomer={fetchCustomer}
          getConsentOptions={getConsentOptions}
          history={history}
          initials={() => initials()}
          isMobile={isMobile}
          onLogout={logout}
          open={openProfilePanel}
          profileMode={rentalMode && rentalMode.rental_mode}
          selectedMarket={selectedMarket}
          togglePaymentModal={(status) => togglePaymentModal(status)}
        />
      )}
      {openPaymentModal && (
        <PaymentMethodModal
          closeModal={() => togglePaymentModal(false)}
          handleNext={onPostPaymentMethodCreation}
          openModal={openPaymentModal}
          returnUrl={returnUrlForStripe}
        />
      )}
    </React.Fragment>
  )
}

export default withRouter(injectIntl(HomePage))
